// src/features/developers/samples.ts
//
// Code samples of an HTTP call, in the five languages, generated from ONE description of the call:
// that is what keeps the five from drifting (same fields, same values, same treatment of an error).
//
// What they produce runs as pasted: credentials come from the environment, `{{baseUrl}}` stands
// for the address (the page replaces it with the environment being looked at), each language uses
// its standard tooling, and a failure prints what the API answered. A call that pages is written
// as a loop in the four languages that can (cURL shows the first page and says how to continue).
//
// Strings are assembled with plain concatenation on purpose: a dollar sign next to a brace, or a
// backtick, inside a template literal would silently print the wrong thing.

import type { RecipeLanguage } from './types';

export type CredentialKind = 'apiKey' | 'sourceKey';

export interface HttpCall {
  method: 'GET' | 'POST';
  /** With its query string, without the page: `/api/v2/data/properties/ID/check-ins?from=2026-09-01&pageSize=50`. */
  path: string;
  body?: unknown;
  /** Defaults to `application/json`. */
  contentType?: string;
  /** Which of the two keys the call sends. */
  credential: CredentialKind;
  /** One line above the call, only where the API surprises. */
  note?: string;
  /** What to print, as a dotted path inside the parsed answer: `data`. Empty prints all of it. */
  show?: string;
  /** Read page after page until `paging.total` items were collected, then print the items. */
  paged?: boolean;
}

export interface Credentials {
  apiKey: string;
  sourceKey: string;
}

export const DEFAULT_CREDENTIALS: Credentials = { apiKey: 'INSIGHT_API_KEY', sourceKey: 'INSIGHT_SOURCE_KEY' };

const HEADER_OF: Record<CredentialKind, string> = { apiKey: 'X-Api-Key', sourceKey: 'X-Source-Key' };

const indent = (text: string, spaces: number): string =>
  text
    .split('\n')
    .map((line, i) => (i === 0 || line === '' ? line : ' '.repeat(spaces) + line))
    .join('\n');

const json = (value: unknown): string => JSON.stringify(value, null, 2);

const steps = (show?: string): string[] => (show ? show.split('.').filter(Boolean) : []);

/** A Python literal: JSON with True/False/None. */
const python = (value: unknown, depth = 0): string => {
  const pad = '    '.repeat(depth + 1);
  const end = '    '.repeat(depth);
  if (value === null || value === undefined) return 'None';
  if (typeof value === 'boolean') return value ? 'True' : 'False';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return JSON.stringify(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    if (value.every((v) => typeof v !== 'object' || v === null)) return '[' + value.map((v) => python(v)).join(', ') + ']';
    return '[\n' + value.map((v) => pad + python(v, depth + 1)).join(',\n') + ',\n' + end + ']';
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) return '{}';
  return '{\n' + entries.map(([k, v]) => pad + JSON.stringify(k) + ': ' + python(v, depth + 1)).join(',\n') + ',\n' + end + '}';
};

/** A PHP array, short syntax, single quotes (nothing is interpolated inside). */
const php = (value: unknown, depth = 0): string => {
  const pad = '    '.repeat(depth + 1);
  const end = '    '.repeat(depth);
  const quote = (s: string): string => "'" + s.replace(/\\/g, '\\\\').replace(/'/g, "\\'") + "'";
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return quote(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    if (value.every((v) => typeof v !== 'object' || v === null)) return '[' + value.map((v) => php(v)).join(', ') + ']';
    return '[\n' + value.map((v) => pad + php(v, depth + 1)).join(',\n') + ',\n' + end + ']';
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) return 'new stdClass()';
  return '[\n' + entries.map(([k, v]) => pad + quote(k) + ' => ' + php(v, depth + 1)).join(',\n') + ',\n' + end + ']';
};

const csAccess = (path: string[]): string => path.map((p) => '.GetProperty("' + p + '")').join('');
const nodeAccess = (path: string[]): string => path.map((p) => '.' + p).join('');
const pyAccess = (path: string[]): string => path.map((p) => '["' + p + '"]').join('');
const phpAccess = (path: string[]): string => path.map((p) => "['" + p + "']").join('');

export const httpSamples = (call: HttpCall, credentials: Credentials = DEFAULT_CREDENTIALS): Record<RecipeLanguage, string> => {
  const url = '{{baseUrl}}' + call.path;
  const hasBody = call.body !== undefined;
  const contentType = call.contentType ?? 'application/json';
  const header = HEADER_OF[call.credential];
  const variable = credentials[call.credential];
  const shown = steps(call.show);
  const sep = call.path.includes('?') ? '&' : '?';
  const method = call.method[0] + call.method.slice(1).toLowerCase();

  // cURL ------------------------------------------------------------------------------------
  const curlNote = call.paged ? 'Page 1 of the list. Repeat with page=2, 3 ... until you have paging.total items (the other languages loop for you).' : call.note;
  const curlUrl = call.paged ? url + sep + 'page=1' : url;
  const curlLines = [(call.method === 'GET' ? 'curl -sS "' : 'curl -sS -X ' + call.method + ' "') + curlUrl + '"'];
  curlLines.push('-H "' + header + ': $' + variable + '"');
  if (hasBody) {
    curlLines.push('-H "Content-Type: ' + contentType + '"');
    curlLines.push("-d '" + json(call.body).replace(/'/g, "'\\''") + "'");
  }
  const curl = (curlNote ? '# ' + curlNote + '\n' : '') + curlLines.join(' \\\n  ');

  // C# ----------------------------------------------------------------------------------------
  const csHead = [
    'using System.Text;',
    'using System.Text.Json;',
    '',
    'using var http = new HttpClient();',
    'http.DefaultRequestHeaders.Add("' + header + '", Environment.GetEnvironmentVariable("' + variable + '"));',
    '',
    ...(call.note ? ['// ' + call.note] : []),
  ];
  let csharp: string;
  if (call.paged) {
    csharp = [
      ...csHead,
      'var items = new List<JsonElement>();',
      'for (var page = 1; ; page++)',
      '{',
      '    using var response = await http.GetAsync("' + url + sep + 'page=" + page);',
      '    var text = await response.Content.ReadAsStringAsync();',
      '    if (!response.IsSuccessStatusCode)',
      '    {',
      '        Console.Error.WriteLine((int)response.StatusCode + ": " + text);',
      '        return;',
      '    }',
      '',
      '    using var document = JsonDocument.Parse(text);',
      '    var data = document.RootElement.GetProperty("data");',
      '    foreach (var item in data.EnumerateArray()) items.Add(item.Clone());',
      '    var total = document.RootElement.GetProperty("paging").GetProperty("total").GetInt32();',
      '    if (data.GetArrayLength() == 0 || items.Count >= total) break;',
      '}',
      '',
      'foreach (var item in items) Console.WriteLine(item);',
    ].join('\n');
  } else {
    csharp = [
      ...csHead,
      ...(hasBody
        ? [
            'var body = """',
            json(call.body),
            '""";',
            'using var request = new HttpRequestMessage(HttpMethod.' + method + ', "' + url + '")',
            '{',
            '    Content = new StringContent(body, Encoding.UTF8, "' + contentType + '"),',
            '};',
          ]
        : ['using var request = new HttpRequestMessage(HttpMethod.' + method + ', "' + url + '");']),
      'using var response = await http.SendAsync(request);',
      'var text = await response.Content.ReadAsStringAsync();',
      'if (!response.IsSuccessStatusCode)',
      '{',
      '    Console.Error.WriteLine((int)response.StatusCode + ": " + text);',
      '    return;',
      '}',
      '',
      'using var document = JsonDocument.Parse(text);',
      'Console.WriteLine(document.RootElement' + csAccess(shown) + ');',
    ].join('\n');
  }

  // Node.js (18+, ES module, built-in fetch) ------------------------------------------------
  const nodeHeaders = ['"' + header + '": process.env.' + variable, ...(hasBody ? ['"Content-Type": "' + contentType + '"'] : [])];
  const nodeHead = call.note ? ['// ' + call.note] : [];
  let node: string;
  if (call.paged) {
    node = [
      ...nodeHead,
      'const items = [];',
      'for (let page = 1; ; page += 1) {',
      '  const response = await fetch("' + url + sep + 'page=" + page, {',
      '    headers: { ' + nodeHeaders.join(', ') + ' },',
      '  });',
      '  const text = await response.text();',
      '  if (!response.ok) {',
      '    console.error(response.status + ": " + text);',
      '    process.exit(1);',
      '  }',
      '',
      '  const body = JSON.parse(text);',
      '  items.push(...body.data);',
      '  if (body.data.length === 0 || items.length >= body.paging.total) break;',
      '}',
      '',
      'console.log(items);',
    ].join('\n');
  } else {
    node = [
      ...nodeHead,
      'const response = await fetch("' + url + '", {',
      '  method: "' + call.method + '",',
      '  headers: {',
      ...nodeHeaders.map((h) => '    ' + h + ','),
      '  },',
      ...(hasBody ? ['  body: JSON.stringify(' + indent(json(call.body), 2) + '),'] : []),
      '});',
      'const text = await response.text();',
      'if (!response.ok) {',
      '  console.error(response.status + ": " + text);',
      '  process.exit(1);',
      '}',
      '',
      'console.log(JSON.parse(text)' + nodeAccess(shown) + ');',
    ].join('\n');
  }

  // Python (requests) -----------------------------------------------------------------------
  const pyHeaders = ['"' + header + '": os.environ["' + variable + '"]', ...(hasBody ? ['"Content-Type": "' + contentType + '"'] : [])];
  const pyHead = ['import os', 'import sys', 'import requests', '', ...(call.note ? ['# ' + call.note] : [])];
  let pythonCode: string;
  if (call.paged) {
    pythonCode = [
      ...pyHead,
      'items = []',
      'page = 1',
      'while True:',
      '    response = requests.get(',
      '        "' + url + sep + 'page=" + str(page),',
      '        headers={' + pyHeaders.join(', ') + '},',
      '        timeout=30,',
      '    )',
      '    if not response.ok:',
      '        sys.exit(str(response.status_code) + ": " + response.text)',
      '',
      '    body = response.json()',
      '    items.extend(body["data"])',
      '    if not body["data"] or len(items) >= body["paging"]["total"]:',
      '        break',
      '    page += 1',
      '',
      'print(items)',
    ].join('\n');
  } else {
    pythonCode = [
      ...pyHead,
      'response = requests.' + call.method.toLowerCase() + '(',
      '    "' + url + '",',
      '    headers={',
      ...pyHeaders.map((h) => '        ' + h + ','),
      '    },',
      ...(hasBody ? ['    data=json.dumps(' + indent(python(call.body), 4) + '),'] : []),
      '    timeout=30,',
      ')',
      'if not response.ok:',
      '    sys.exit(str(response.status_code) + ": " + response.text)',
      '',
      'print(response.json()' + pyAccess(shown) + ')',
    ].join('\n');
    if (hasBody) pythonCode = pythonCode.replace('import os\n', 'import json\nimport os\n');
  }

  // PHP (curl_*) ----------------------------------------------------------------------------
  const phpHeaders = ["'" + header + ": ' . getenv('" + variable + "')", ...(hasBody ? ["'Content-Type: " + contentType + "'"] : [])];
  const phpHead = ['<?php', ...(call.note ? ['// ' + call.note] : [])];
  let phpCode: string;
  if (call.paged) {
    phpCode = [
      ...phpHead,
      '$items = [];',
      'for ($page = 1; ; $page++) {',
      "    $curl = curl_init('" + url + sep + "page=' . $page);",
      '    curl_setopt_array($curl, [',
      '        CURLOPT_RETURNTRANSFER => true,',
      '        CURLOPT_HTTPHEADER => [' + phpHeaders.join(', ') + '],',
      '    ]);',
      '    $text = curl_exec($curl);',
      '    $status = curl_getinfo($curl, CURLINFO_RESPONSE_CODE);',
      '    curl_close($curl);',
      '    if ($status >= 400) {',
      "        fwrite(STDERR, $status . ': ' . $text . PHP_EOL);",
      '        exit(1);',
      '    }',
      '',
      '    $body = json_decode($text, true);',
      "    $items = array_merge($items, $body['data']);",
      "    if (count($body['data']) === 0 || count($items) >= $body['paging']['total']) {",
      '        break;',
      '    }',
      '}',
      '',
      'print_r($items);',
    ].join('\n');
  } else {
    phpCode = [
      ...phpHead,
      "$curl = curl_init('" + url + "');",
      'curl_setopt_array($curl, [',
      "    CURLOPT_CUSTOMREQUEST => '" + call.method + "',",
      '    CURLOPT_RETURNTRANSFER => true,',
      '    CURLOPT_HTTPHEADER => [',
      ...phpHeaders.map((h) => '        ' + h + ','),
      '    ],',
      ...(hasBody ? ['    CURLOPT_POSTFIELDS => json_encode(' + indent(php(call.body), 4) + '),'] : []),
      ']);',
      '$text = curl_exec($curl);',
      '$status = curl_getinfo($curl, CURLINFO_RESPONSE_CODE);',
      'curl_close($curl);',
      'if ($status >= 400) {',
      "    fwrite(STDERR, $status . ': ' . $text . PHP_EOL);",
      '    exit(1);',
      '}',
      '',
      'print_r(json_decode($text, true)' + phpAccess(shown) + ');',
    ].join('\n');
  }

  return { curl, csharp, node, python: pythonCode, php: phpCode };
};
